import {
  Component,
  signal,
  inject,
  input,
  effect,
  output,
  computed,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

// Angular Material Imports
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';

// Lucide Icons
import { LucideAngularModule, Info } from 'lucide-angular';
import { ItemSelectorComponent } from './item-selector/item-selector.component';
import { Subscription, firstValueFrom, lastValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ListOptionManager } from './list-option-manager/list-option-manager';
import { SurveyListOptionService } from '../survey-list-option/survey-list-option';
import {
  ListType,
  SurveyList,
  SurveyListOption,
} from '@hyper-inferno/survey-elements-core';
import { DynamicListPreview } from './list-option-manager/dynamic-list-preview/dynamic-list-preview';
import { AddItemDialogComponent } from './add-item-dialog/add-item-dialog.component';
import { SurveyListService } from '../survey-list-service/survey-list';
import { DataStoreService } from '../data-store-service/data-store.service';

@Component({
  selector: 'lib-list-manager',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatIconModule,
    LucideAngularModule,
    ItemSelectorComponent,
    MatDialogModule,
    ListOptionManager,
    DynamicListPreview,
  ],
  templateUrl: './list-manager.html',
  styleUrl: './list-manager.scss',
})
export class ListManagerComponent {
  readonly configurationSettingEvent = output<string>();
  private listService = inject(SurveyListService);
  private dataStoreService = inject(DataStoreService);

  //
  extraButton = input<string | undefined>();
  lists = signal<SurveyList[]>([]);
  activeListId = signal<string | undefined>(undefined);
  activeList = computed(() =>
    this.lists().find((l) => l.id === this.activeListId()),
  );
  dynamicPreviewItems: SurveyListOption[] = [];
  searchTerm = signal<string>('');

  filteredLists = computed(() => {
    const term = this.searchTerm().toLowerCase();

    const lists = term
      ? this.lists().filter((l) =>
        l.name?.toLowerCase().includes(term)
      )
      : this.lists();

    return [...lists].sort((a, b) =>
      (a.name ?? '').localeCompare(
        b.name ?? '',
        undefined,
        {
          numeric: true,
          sensitivity: 'base',
        }
      )
    );
  });
  //
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  es_entityName = input.required<string>();
  elementId = input.required<string | undefined>();
  questionId = input<number | null>(null);
  surveyId = signal<number | undefined>(undefined);
  readonly InfoIcon = Info;

  listSet = output<string | undefined>();
  private listOptionService = inject(SurveyListOptionService);
  private subscriptions = new Subscription();
  listType = ListType;
  constructor() {
    effect(() => {
      const list_id = this.elementId();
      if (list_id) {
        this.activeListId.set(this.elementId());
        this.listSet.emit(list_id);
        // this.changeList(list_id);
      }
      const surveyId = this.dataStoreService.surveyId();
      if (surveyId) {
        this.loadLists(surveyId);
      }
    });
    this.listService.refresh$.subscribe(() => {
      // Reload lists when refresh is triggered
      const surveyId = this.surveyId();
      if (surveyId) {
        this.loadLists(surveyId);
      }
    });

    effect(() => {
      const activeListId = this.activeListId();
      if (activeListId) {
        this.updateDynamicPreviewItems(activeListId);
      }
    });
  }
  changeList(listId: string) {
    this.activeListId.set(listId);
    this.listSet.emit(listId);
    const currentList = this.lists().find((l) => l.id === listId);

    if (currentList?.list_type === ListType.CONSTRUCTED_LIST) {
      this.updateDynamicPreviewItems(listId);
    } else {
      this.dynamicPreviewItems = [];
    }
  }

  @ViewChild(ListOptionManager) listOptionManager!: ListOptionManager;
  ngOnInit() {
    const activeSurveyId = this.dataStoreService.surveyId();
    this.surveyId.set(activeSurveyId ? Number(activeSurveyId) : undefined);

    this.loadLists(activeSurveyId!);

    if (this.elementId()) {
      this.activeListId.set(this.elementId());
      this.listSet.emit(this.activeListId());
    }
    //
    this.subscriptions.add(
      this.listOptionService.listUpdated$.subscribe((listId) => {
        const currentListId = this.activeListId();
        // If the updated list is the currently active list, refresh the options
        if (currentListId === listId && this.listOptionManager) {
          this.listOptionManager.reloadItems(listId);
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  async updateDynamicPreviewItems(listId: string) {
    const items = await this.getdynamicPreviewItems(listId);
    this.dynamicPreviewItems = items;
  }

  async getdynamicPreviewItems(list_id: string): Promise<SurveyListOption[]> {
    const lists = this.lists();
    const currentList = lists?.find((list) => list.id === list_id);

    if (!currentList) {
      return [];
    }
    let options: SurveyListOption[] = [];

    if (
      currentList.list_type === ListType.CONSTRUCTED_LIST &&
      currentList.primary_list_id
    ) {
      options = await firstValueFrom(
        this.listOptionService.getListOptions(currentList.primary_list_id),
      );
    } else {
      options = await firstValueFrom(
        this.listOptionService.getListOptions(list_id),
      );
    }
    return options.sort((a, b) => Number(a.option_key) - Number(b.option_key));
  }

  handleEditDynamicList() {
    const activeList = this.activeList();
    const activeListId = this.activeListId();
    if (!activeListId || !activeList) return;

    const dialogRef = this.dialog.open(AddItemDialogComponent, {
      width: '900px',
      data: {
        listType: ListType.CONSTRUCTED_LIST,
        surveyId: this.surveyId(),
        selectedListId: activeListId,
        es_entityName: this.es_entityName(),
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        if (this.surveyId()) {
          this.loadLists(this.surveyId()!);
        }
        this.updateDynamicPreviewItems(activeListId);
      }
    });
  }

  async loadLists(survey_id: number) {
    try {
      const lists = await lastValueFrom(this.listService.getList(survey_id));
      this.lists.set(lists);
    } catch (error) {
      console.error('Error loading lists:', error);
    }
  }

  handleConfigurationSetting(id: string) {
    this.configurationSettingEvent.emit(id);
  }
}
